import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Table, Button, Input, Dropdown, Modal, MenuProps } from "antd";
import {
    PlusOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    MoreOutlined,
} from "@ant-design/icons";
import Layout from "../../Layout";
import useAuthStore from "../../store/authStore";
import AddStockModal from "./AddModal";
import useTableColumnSettings from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

const { Search } = Input;

interface Stock {
    id: number;
    name: string;
    phone: string;
    email: string;
    province: string;
    district: string;
    ward: string;
    address: string;
    isDefault: boolean;
}

const mockStocks: Stock[] = [
    {
        id: 1,
        name: "buso",
        phone: "0987654321",
        email: "huan@gmail.com",
        province: "Hồ Chí Minh",
        district: "QUẬN 5",
        ward: "PHƯỜNG 1",
        address: "tphcm",
        isDefault: true,
    },
];

export default function StockPage() {
  const { t } = useTranslation();
    // Phân quyền thao tác: chỉ có quyền xem thì không hiện nút thêm/sửa/xóa
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("products.create");
    const canEdit = isSuperAdmin || hasPermission("products.edit");
    const canDelete = isSuperAdmin || hasPermission("products.delete");
    const [stocks, setStocks] = useState<Stock[]>(mockStocks);
    const [editingStock, setEditingStock] = useState<Stock | null>(null);
    const [openModal, setOpenModal] = useState(false);

    const handleEdit = (record: any) => {
        setEditingStock(record);
        setOpenModal(true);
    };

    const handleSave = (values: any) => {

        if (editingStock) {

            setStocks((prev) =>
                prev.map((item) =>
                    item.id === editingStock.id
                        ? { ...item, ...values }
                        : item
                )
            );

            setEditingStock(null);

        } else {

            setStocks((prev) => [
                {
                    id: Date.now(),
                    ...values
                },
                ...prev
            ]);

        }
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: "Delete Stock",
            content: "Are you sure you want to delete this stock?",
            okText: "Delete",
            okType: "danger",
            cancelText: "Cancel",
            onOk() {
                setStocks((prev) => prev.filter((item) => item.id !== id));
            },
        });
    };

    const columns = [
        {
            title: "Tên",
            dataIndex: "name",
            render: (text: string) => (
                <span className="text-[#3e91ab] font-medium">{text}</span>
            ),
        },
        {
            title: "Số điện thoại",
            dataIndex: "phone",
        },
        {
            title: "Email",
            dataIndex: "email",
        },
        {
            title: "Tỉnh/Thành phố",
            dataIndex: "province",
        },
        {
            title: "Quận/Huyện",
            dataIndex: "district",
        },
        {
            title: "Phường/Xã",
            dataIndex: "ward",
        },
        {
            title: "Địa chỉ",
            dataIndex: "address",
        },
        {
            title: "Mặc định",
            dataIndex: "isDefault",
            render: (value: boolean) => (value ? "Có" : "Không"),
        },
        {
            key: "actions",
            title: "Hành động",
            fixed: "right" as const,
            lockVisible: true,
            render: (_: any, record: Stock) => {
                const items: MenuProps["items"] = [
                    ...(canEdit ? [{
                        key: "edit",
                        icon: <EditOutlined />,
                        label: "Chỉnh sửa",
                        onClick: () => handleEdit(record)
                    }] : []),
                    ...(canDelete ? [{
                        key: "delete",
                        icon: <DeleteOutlined />,
                        label: "Xóa",
                        danger: true,
                        onClick: () => handleDelete(record.id),
                    }] : []),
                ];

                return (
                    <Dropdown menu={{ items }} trigger={["click"]}>
                        <Button icon={<MoreOutlined />} />
                    </Dropdown>
                );
            },
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "warehouse_list",
        (canEdit || canDelete) ? columns : columns.filter((c: any) => c.key !== "actions")
    );

    return (
        <Layout>
            <Helmet>
                <title>{t('page.warehouses')} | Khothi</title>
            </Helmet>
            <>
                <div className="flex justify-between mb-4">
                    <h2 className="text-lg font-semibold text-000000e0">Chi Nhánh</h2>

                    <div className="flex gap-2">
                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                className="bg-[#3e91ab] border-[#3e91ab]"
                                onClick={() => setOpenModal(true)}
                            >
                                Thêm
                            </Button>
                        )}
                        <ColumnSettingButton {...columnSettingProps} />
                    </div>
                </div>

                <Table
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={stocks}
                    rowKey="id"
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                />
                <AddStockModal
                    open={openModal}
                    setOpen={setOpenModal}
                    onCreate={handleSave}
                    editingStock={editingStock}
                />
            </>
        </Layout>
    );
}